import { css, html } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { property } from "lit/decorators/property.js";
import { BsCard } from "./card";
import "./form-item";
import "./text";
import "./chip";
import "./title";
import { when } from "lit/directives/when.js";
import { isVideo } from "./section-split";
import { translate } from "lit-translate";

@customElement("bs-quote-card")
export class BsQuoteCard extends BsCard {
	static get styles() {
		return [
			...super.styles,
			css`
				:host {
					width: 100%;
					text-align: left;
					display: flex;
					align-items: center;
					flex-direction: row;
					overflow: hidden;
					position: relative;
				}

				:host([flip]) {
					flex-direction: row-reverse;
				}

				:host(:hover) #cta-container {
					opacity: 1;
				}

				:host(:hover) #cta {
					transform: scale(1);
				}

				#left {
					padding: var(--space-l) var(--space-xl);
					display: flex;
					flex-direction: column;
					gap: var(--space-l);
					flex-grow: 1;
				}

				#avatar-container {
					display: flex;
					gap: var(--space-s);
					align-items: center;
				}

				#avatar {
					width: 40px;
					height: 40px;
					object-fit: cover;
					border-radius: 100%;
				}

				#right {
					display: flex;
					background: var(--shade-light);
				}

				#img,
				#video {
					width: 400px;
					height: 300px;
					object-fit: cover;
				}

				#video {
					object-fit: contain;
					padding: var(--space-l);
				}

				#cta-container {
					position: absolute;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: var(--space-l);
					background: linear-gradient(0deg, var(--background), rgba(255, 255, 255, 0));
					left: 0;
					bottom: 0;
					width: 100%;
					opacity: 0;
					transition: opacity 120ms ease-out;
					--form-item-bg: var(--primary-normal);
					--form-item-color: var(--primary-contrast-normal);
				}

				#cta {
					transition: transform 120ms ease-out;
					transform: scale(0.8);
				}

				#chip {
					margin-top: var(--space-m);
				}

				#headline {
					text-wrap: balance;
				}

				@media (max-width: 800px) {
					:host,
					:host([flip]) {
						flex-direction: column;
						align-items: stretch;
					}

					#img,
					#video {
						width: 100%;
					}
				}
			`
		];
	}

	@property({ type: String }) headline?: string;
	@property({ type: String }) chip?: string;
	@property({ type: String }) text?: string;
	@property({ type: String }) label?: string;
	@property({ type: String }) name?: string;
	@property({ type: String }) avatar?: string;
	@property({ type: String }) illustration?: string;
	@property({ type: String }) cta = translate("Get started with Ideamap");
	@property({ type: Boolean, reflect: true }) flip = false;

	render() {
		const { headline, chip, illustration, cta, text, label, name, avatar } = this;
		return html`
			<div id="left">
				<div>
					<bs-title id="headline" size="large" margin>${headline}</bs-title>
					<bs-text size="large">${text}</bs-text>
					${when(chip, () => html`<bs-chip id="chip"><bs-text>${chip}</bs-text></bs-chip>`)}
				</div>
				${when(
					avatar || name || label,
					() => html`
						<div id="avatar-container">
							${when(avatar, () => html`<img id="avatar" alt="Avatar" src="${avatar}" loading="lazy" />`)}
							<div>
								<bs-title size="small">${name}</bs-title>
								<bs-text label>${label}</bs-text>
							</div>
						</div>
					`
				)}
			</div>
			<div id="right">
				${when(
					illustration != null && isVideo(illustration),
					() =>
						html`<video id="video" autoplay loop muted playsinline alt="Quote illustration" src="${illustration}" />`,
					() => html` <img id="img" alt="Quote illustration" src="${illustration}" loading="lazy" /> `
				)}
			</div>
			<div id="cta-container">
				<bs-form-item id="cta">
					<button slot="input">${cta}</button>
				</bs-form-item>
			</div>
		`;
	}
}
